import { useEffect, useRef, useState } from 'react';
import { FaCheck, FaChevronDown } from 'react-icons/fa';
import { cn } from '../../../utils/cn';

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Secondary line, e.g. a broker's agency. */
  detail?: string;
}

export interface MultiSelectProps {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  /** Shown on the trigger when nothing is selected. */
  allLabel?: string;
  disabled?: boolean;
}

/**
 * Checkbox dropdown for the reporting filters. An empty selection means "all",
 * which keeps the default state of every filter a single, obvious thing.
 */
export const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  options,
  selected,
  onChange,
  allLabel = 'All',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);
  };

  const summary =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? (options.find(option => option.value === selected[0])?.label ?? '1 selected')
        : `${selected.length} selected`;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        disabled={disabled || options.length === 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm transition-colors sm:w-48',
          'hover:bg-[var(--color-hover-background)] disabled:cursor-not-allowed disabled:opacity-50'
        )}
        style={{
          backgroundColor: 'var(--color-background-paper)',
          borderColor: selected.length ? 'var(--color-primary-500)' : 'var(--color-border-light)',
          color: 'var(--color-text-primary)',
        }}
      >
        <span className="min-w-0 truncate text-left">
          <span
            className="text-xs font-semibold uppercase tracking-wide"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {label}
          </span>
          <span className="block truncate">{summary}</span>
        </span>
        <FaChevronDown className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
      </button>

      {open && (
        <div
          className="absolute z-20 mt-1 max-h-72 w-full min-w-[15rem] overflow-y-auto rounded-lg border py-1 shadow-lg"
          style={{
            backgroundColor: 'var(--color-background-paper)',
            borderColor: 'var(--color-border-light)',
          }}
          role="listbox"
          aria-multiselectable="true"
          aria-label={label}
        >
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full px-3 py-1.5 text-left text-xs font-semibold underline"
              style={{ color: 'var(--color-primary-600)' }}
            >
              Clear {label.toLowerCase()}
            </button>
          )}
          {options.map(option => {
            const isSelected = selected.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => toggle(option.value)}
                className="flex w-full items-start gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-[var(--color-hover-background)]"
              >
                <span
                  className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border"
                  style={{
                    borderColor: isSelected
                      ? 'var(--color-primary-500)'
                      : 'var(--color-border-medium)',
                    backgroundColor: isSelected ? 'var(--color-primary-500)' : 'transparent',
                    color: '#ffffff',
                  }}
                  aria-hidden="true"
                >
                  {isSelected && <FaCheck className="h-2 w-2" />}
                </span>
                <span className="min-w-0">
                  <span className="block truncate" style={{ color: 'var(--color-text-primary)' }}>
                    {option.label}
                  </span>
                  {option.detail && (
                    <span
                      className="block truncate text-xs"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      {option.detail}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
